/**
 * The site photo carousel on the head-office dashboards. 30-09-2026, migration 0185.
 *
 * WHY IT EXISTS
 *   Head office arranges the materials and services every site runs on, and
 *   rarely sees one. A calm carousel of current work-in-progress photos shows
 *   them what that work is building - and any of them can refresh a site's
 *   photo from here, so it keeps itself current.
 *
 * THE RULES (the database enforces them - rpc_add_site_photo)
 *   * one current photo per ACTIVE site, so the carousel never holds more
 *     photos than there are active sites; a new photo of a site replaces its
 *     old one, which is kept on record
 *   * head office only (isHoRole here; is_ho_user() in the database decides)
 *
 * NEVER SLOWS THE DASHBOARD DOWN
 *   * photos arrive pre-shrunk (imageEngine.ts: WebP, 1280 x 720, ~60-150 KB)
 *   * only the photo on screen and the next one are loaded; the rest are
 *     fetched as the carousel reaches them
 *   * the secure links are reused for fifty minutes, so the two-minute
 *     refresh never downloads a photo twice
 *
 * THE LOOK: it fills the grid cell the dashboard gives it (a third of the
 * width on a laptop or tablet, full width on a phone); a 1.2 s cross-fade
 * every 7 s; a very slow zoom; pauses under the pointer or a finger and in a
 * hidden tab; "reduce motion" gets a plain fade. CSS only - no library.
 *
 * SHARED between the Materials and Service dashboards by decision (Mrinmoy,
 * 30-09-2026); on the shared list in scripts/check-module-boundary-data.py.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { ChevronLeft, ChevronRight, ImagePlus, MapPin, RotateCcw, Trash2, Wand2, X } from 'lucide-react'
import { Alert, Field, Input, Modal, Select, Spinner, useToast } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { usePeople, useSites } from '@/lib/data'
import { isHoRole } from '@/lib/domain'
import { ddmmyyyy } from '@/lib/format'
import { supabase } from '@/lib/supabase'
import { kb, loadSource, prepare, type Prepared, type Source } from './imageEngine'

const BUCKET = 'site-photos'
const SLIDE_MS = 7000
const FADE_MS = 1200
// 30-09-2026, "the app is slower since the photos went in": the secure links
// used to live only as long as the dashboard was on screen, so every return
// to it made new links - which the browser treats as new files and downloads
// again. They are now kept for the whole browser session, valid 12 hours and
// reused for 11, so each photo downloads once a day, not once a visit.
const LINK_TTL_S = 12 * 3600
const LINK_REUSE_MS = 11 * 3600 * 1000
const LINK_STORE = 'oval.sitePhotoLinks'
const links: Map<string, { url: string; at: number }> = (() => {
  try { return new Map(Object.entries(JSON.parse(sessionStorage.getItem(LINK_STORE) ?? '{}'))) }
  catch { return new Map() }
})()
const saveLinks = () => {
  try { sessionStorage.setItem(LINK_STORE, JSON.stringify(Object.fromEntries(links))) } catch { /* private mode: fine */ }
}

interface Slide {
  id: string
  site_id: string
  site_name: string
  file_path: string
  caption: string | null
  uploaded_by: string
  uploaded_at: string
  url: string
}

/**
 * Who sees the carousel: head office, and - since 0187 - the Project
 * Managers, who work from head office too. A Project Manager sees every site's
 * photo but adds or replaces one only for his own sites. The database decides
 * (fn_may_use_site_photos, rpc_add_site_photo); this only hides the frame.
 */
export const maySeeSitePhotos = (role?: string | null) =>
  isHoRole(role as any) || role === 'project_manager'

/**
 * 30-09-2026: on the administrator's, the CXO's and the CMD's dashboards the
 * board panel sits beside everything else, so the carousel is small. For them
 * a click opens the photos full size in a see-through lightbox. Everyone else
 * keeps the carousel exactly as it was.
 */
const LIGHTBOX_ROLES = ['admin', 'cxo', 'cmd']

const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function SitePhotoCarousel({ className }: { className?: string }) {
  const { profile } = useAuth()
  const toast = useToast()
  const people = usePeople()
  const isHo = maySeeSitePhotos(profile?.role)

  const [slides, setSlides] = useState<Slide[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [at, setAt] = useState(0)
  const [paused, setPaused] = useState(false)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Slide | null>(null)
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0, 1]))
  const [viewer, setViewer] = useState<number | null>(null)
  const opensLightbox = LIGHTBOX_ROLES.includes(profile?.role ?? '')
  const still = useMemo(reducedMotion, [])
  // Animate only while it can be seen: on screen and in a visible tab. A zoom
  // nobody is looking at still costs the laptop's graphics chip every frame.
  const box = useRef<HTMLElement>(null)
  const [inView, setInView] = useState(true)
  const [tabShown, setTabShown] = useState(() => !document.hidden)
  useEffect(() => {
    const onVis = () => setTabShown(!document.hidden)
    document.addEventListener('visibilitychange', onVis)
    const el = box.current
    const io = el && 'IntersectionObserver' in window
      ? new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.1 }) : null
    if (io && el) io.observe(el)
    return () => { document.removeEventListener('visibilitychange', onVis); io?.disconnect() }
  }, [])
  const moving = !still && inView && tabShown

  const load = useCallback(async () => {
    // Active photos of ACTIVE sites only - a site switched off in the master
    // drops out of the carousel without anybody touching its photo.
    const { data, error: e } = await supabase.from('site_photos')
      .select('id, site_id, file_path, caption, uploaded_by, uploaded_at, sites!inner(site_name, is_active)')
      .eq('is_active', true).eq('sites.is_active', true)
      .order('uploaded_at', { ascending: false })
    if (e) { setError(e.message); setSlides([]); return }
    const rows = (data ?? []) as any[]

    // Sign only what is new or about to expire; reuse the rest, so the browser
    // cache serves every photo it has already shown.
    const now = Date.now()
    const need = rows.map((r) => r.file_path)
      .filter((p: string) => { const l = links.get(p); return !l || now - l.at > LINK_REUSE_MS })
    if (need.length) {
      const { data: signed, error: se } = await supabase.storage.from(BUCKET).createSignedUrls(need, LINK_TTL_S)
      if (se) { setError(se.message); setSlides([]); return }
      for (const s of signed ?? []) if (s.path && s.signedUrl) links.set(s.path, { url: s.signedUrl, at: now })
      saveLinks()
    }
    setSlides(rows.filter((r) => links.get(r.file_path)).map((r) => ({
      id: r.id, site_id: r.site_id, site_name: r.sites?.site_name ?? r.site_id,
      file_path: r.file_path, caption: r.caption, uploaded_by: r.uploaded_by,
      uploaded_at: r.uploaded_at, url: links.get(r.file_path)!.url,
    })))
    setError(null)
  }, [])

  useEffect(() => {
    if (!isHo) return
    // The dashboard's own figures first: the carousel starts only once the
    // browser is idle (or after 1.5 s at the latest), so its query and its
    // first photo never compete with "Waiting on you" on a slow connection.
    const w = window as any
    const start = () => void load()
    const idle = w.requestIdleCallback ? w.requestIdleCallback(start, { timeout: 1500 }) : window.setTimeout(start, 800)
    const t = window.setInterval(() => { if (!document.hidden) void load() }, 120_000)
    const onFocus = () => void load()
    window.addEventListener('focus', onFocus)
    return () => {
      if (w.cancelIdleCallback && w.requestIdleCallback) w.cancelIdleCallback(idle); else window.clearTimeout(idle)
      window.clearInterval(t); window.removeEventListener('focus', onFocus)
    }
  }, [isHo, load])

  const n = slides?.length ?? 0
  useEffect(() => { if (at >= n && n > 0) setAt(0) }, [n, at])
  // Load the photo on screen and the next one - nothing further ahead.
  useEffect(() => {
    if (!n) return
    setSeen((s) => (s.has(at) && s.has((at + 1) % n) ? s : new Set([...s, at, (at + 1) % n])))
  }, [at, n])

  useEffect(() => {
    if (n < 2 || paused || !inView || viewer !== null) return
    const t = window.setInterval(() => { if (!document.hidden) setAt((i) => (i + 1) % n) }, SLIDE_MS)
    return () => window.clearInterval(t)
  }, [n, paused, inView, viewer])

  const go = (d: number) => n && setAt((i) => (i + d + n) % n)
  const hasPhoto = useMemo(() => new Set((slides ?? []).map((s) => s.site_id)), [slides])

  if (!isHo) return null

  const cur = slides && slides[at]
  const mayRemove = (s: Slide) => s.uploaded_by === profile?.id || profile?.role === 'admin'

  return (
    <section ref={box} aria-roledescription="carousel" aria-label="Photos from our sites"
             className={clsx('relative overflow-hidden rounded-xl border border-slate-200 bg-sidebar shadow-card', className)}
             onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
             onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}
             onKeyDown={(e) => { if (e.key === 'ArrowLeft') go(-1); if (e.key === 'ArrowRight') go(1) }}
             tabIndex={0}>
      {/* Always a true 16:9: the width is set by the dashboard (a third of it
          on a tablet or laptop), and the height follows. Stretching it to the
          cards beside it would crop the sides of every photo. */}
      <div className={clsx('relative aspect-video w-full', opensLightbox && n > 0 && 'cursor-zoom-in')}
           title={opensLightbox && n > 0 ? 'Click to see the photos full size' : undefined}
           onClick={(e) => {
             // The buttons on top keep their own jobs; a click on the photo opens it large.
             if (!opensLightbox || !n || (e.target as HTMLElement).closest('button')) return
             setViewer(at)
           }}>
        {slides === null && (
          <div className="absolute inset-0 flex items-center justify-center"><Spinner className="h-5 w-5 text-oval-300" /></div>
        )}

        {slides && slides.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-oval-900 via-sidebar to-sidebar px-5 text-center">
            <MapPin className="h-6 w-6 text-oval-300" />
            <p className="text-sm font-semibold text-white">Our sites, as they are today</p>
            <p className="max-w-xs text-xs text-sidebar-text">
              {error ? `The photos could not be loaded: ${error}`
                : 'No site photos yet. Add a current photo of any site and every head-office dashboard shows it.'}
            </p>
            <button className="btn-primary btn-sm mt-1" onClick={() => setAdding(true)}>
              <ImagePlus className="h-4 w-4" /> Add a site photo
            </button>
          </div>
        )}

        {slides?.map((s, i) => {
          const on = i === at
          return (
            <div key={s.id} aria-hidden={!on} className="absolute inset-0"
                 style={{ opacity: on ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out` }}>
              {seen.has(i) && (
                <img src={s.url} alt={[s.site_name, s.caption].filter(Boolean).join(' — ')}
                     className="h-full w-full object-cover" decoding="async"
                     loading={i === 0 ? 'eager' : 'lazy'}
                     style={!moving ? undefined : {
                       transform: on ? 'scale(1.03)' : 'scale(1)',
                       transition: on ? `transform ${SLIDE_MS + FADE_MS}ms linear` : `transform 0ms linear ${FADE_MS}ms`,
                       transformOrigin: i % 2 ? '30% 40%' : '70% 60%',
                     }} />
              )}
            </div>
          )
        })}

        {cur && (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-sidebar/90 via-sidebar/35 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3" aria-live="polite">
              <div className="min-w-0 text-white">
                <p className="flex items-center gap-1 truncate text-sm font-semibold">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-oval-300" /> {cur.site_name}
                </p>
                {cur.caption && <p className="truncate text-xs text-white/85">{cur.caption}</p>}
                <p className="truncate text-[10px] text-white/60">
                  {people.name({ id: cur.uploaded_by })} · {ddmmyyyy(cur.uploaded_at)}
                  {mayRemove(cur) && (
                    <button className="ml-1.5 inline-flex min-h-0 items-center gap-0.5 text-white/60 hover:text-white hover:underline"
                            onClick={() => setRemoving(cur)} title="Take this photo off the dashboard">
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  )}
                </p>
              </div>
              {n > 1 && (
                <div className="flex shrink-0 items-center gap-1" role="tablist" aria-label="Choose a photo">
                  {slides!.map((s, i) => (
                    <button key={s.id} role="tab" aria-selected={i === at} aria-label={s.site_name}
                            onClick={() => setAt(i)}
                            className={clsx('min-h-0 rounded-full transition-all duration-500',
                              i === at ? 'h-1.5 w-4 bg-oval-400' : 'h-1.5 w-1.5 bg-white/40 hover:bg-white/70')} />
                  ))}
                </div>
              )}
            </div>

            {n > 1 && (['prev', 'next'] as const).map((d) => (
              <button key={d} aria-label={d === 'prev' ? 'Previous photo' : 'Next photo'}
                      onClick={() => go(d === 'prev' ? -1 : 1)}
                      className={clsx('absolute top-1/2 flex min-h-0 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 p-1.5 text-white transition-opacity duration-300 hover:bg-black/55',
                        d === 'prev' ? 'left-2' : 'right-2', paused ? 'opacity-100' : 'opacity-0')}>
                {d === 'prev' ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ))}

            <button onClick={() => setAdding(true)} title="Add a site photo" aria-label="Add a site photo"
                    className="absolute right-2 top-2 inline-flex min-h-0 items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-[11px] font-semibold text-oval-800 shadow-sm transition hover:bg-white">
              <ImagePlus className="h-3.5 w-3.5" /> <span className="hidden lg:inline">Add photo</span>
            </button>
          </>
        )}
      </div>

      {viewer !== null && slides && slides.length > 0 && (
        <PhotoLightbox slides={slides} start={viewer} name={(id) => people.name({ id })}
                       onClose={(k) => { setAt(k); setViewer(null) }} />
      )}
      {adding && (
        <AddSitePhoto hasPhoto={hasPhoto} onClose={() => setAdding(false)}
                      onDone={async () => { setAdding(false); await load(); setAt(0) }} />
      )}
      {removing && (
        <RemoveSitePhoto slide={removing} onClose={() => setRemoving(null)}
                         onDone={async () => { setRemoving(null); toast.success('Taken off the dashboard.'); await load() }} />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// The lightbox: the photos full size over a see-through graphite veil, with
// arrows, left/right keys, a swipe on a touch screen, a counter and a strip of
// thumbnails. Esc, the cross, or a click on the veil closes it and leaves the
// carousel on the photo last looked at. Nothing plays on its own in here.
function PhotoLightbox({ slides, start, name, onClose }: {
  slides: Slide[]; start: number; name: (id: string) => string; onClose: (at: number) => void
}) {
  const [i, setI] = useState(Math.min(start, slides.length - 1))
  const n = slides.length
  const go = useCallback((d: number) => setI((x) => (x + d + n) % n), [n])
  const touch = useRef<number | null>(null)
  const s = slides[i]

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(i)
      else if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === 'ArrowRight') go(1)
    }
    document.addEventListener('keydown', onKey)
    const before = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = before }
  }, [i, go, onClose])

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Site photos"
         className="fixed inset-0 z-50 flex flex-col bg-[#0E1614]/85"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(i) }}
         onTouchStart={(e) => { touch.current = e.touches[0].clientX }}
         onTouchEnd={(e) => {
           if (touch.current == null) return
           const dx = e.changedTouches[0].clientX - touch.current
           touch.current = null
           if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1)
         }}>
      <div className="flex items-center justify-between px-4 py-3 text-white/80" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm tabular-nums">{i + 1} / {n}</span>
        <button aria-label="Close" onClick={() => onClose(i)}
                className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 sm:px-16"
           onClick={(e) => { if (e.target === e.currentTarget) onClose(i) }}>
        <img key={s.id} src={s.url} alt={[s.site_name, s.caption].filter(Boolean).join(' - ')}
             className="oval-lightbox-in max-h-full max-w-full rounded-lg object-contain shadow-2xl" />
        {n > 1 && (['prev', 'next'] as const).map((d) => (
          <button key={d} aria-label={d === 'prev' ? 'Previous photo' : 'Next photo'}
                  onClick={() => go(d === 'prev' ? -1 : 1)}
                  className={clsx('absolute top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/25',
                    d === 'prev' ? 'left-2 sm:left-4' : 'right-2 sm:right-4')}>
            {d === 'prev' ? <ChevronLeft className="h-6 w-6" /> : <ChevronRight className="h-6 w-6" />}
          </button>
        ))}
      </div>

      <div className="px-4 pb-4 pt-3 text-center text-white" onClick={(e) => e.stopPropagation()} aria-live="polite">
        <p className="flex items-center justify-center gap-1.5 text-base font-semibold">
          <MapPin className="h-4 w-4 text-oval-300" /> {s.site_name}
        </p>
        {s.caption && <p className="text-sm text-white/85">{s.caption}</p>}
        <p className="mt-0.5 text-xs text-white/55">{name(s.uploaded_by)} · {ddmmyyyy(s.uploaded_at)}</p>
        {n > 1 && (
          <div className="mx-auto mt-3 flex max-w-full gap-2 overflow-x-auto pb-1 sm:justify-center">
            {slides.map((t, k) => (
              <button key={t.id} aria-label={t.site_name} onClick={() => setI(k)}
                      className={clsx('min-h-0 shrink-0 overflow-hidden rounded-md transition',
                        k === i ? 'ring-2 ring-oval-400' : 'opacity-50 hover:opacity-90')}>
                <img src={t.url} alt="" loading="lazy" className="h-12 w-[85px] object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
function AddSitePhoto({ hasPhoto, onClose, onDone }: {
  hasPhoto: Set<string>; onClose: () => void; onDone: () => Promise<void>
}) {
  const toast = useToast()
  const { profile } = useAuth()
  const { data: sites } = useSites()
  const [siteId, setSiteId] = useState('')
  const [caption, setCaption] = useState('')
  const [src, setSrc] = useState<Source | null>(null)
  const [frameAt, setFrameAt] = useState(0.5)
  const [out, setOut] = useState<Prepared | null>(null)
  const [working, setWorking] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; y: number; at: number } | null>(null)

  // A Project Manager adds photos for the sites he is named on (0187);
  // head office for any active site. rpc_add_site_photo enforces the same.
  const isPm = profile?.role === 'project_manager'
  const active = useMemo(() => sites
    .filter((s: any) => s.is_active !== false && (!isPm || s.project_manager_id === profile?.id))
    .sort((a: any, b: any) => String(a.site_name).localeCompare(String(b.site_name))), [sites, isPm, profile?.id])

  // Free the preview images when the window closes or they are replaced.
  useEffect(() => () => { if (src?.previewUrl) URL.revokeObjectURL(src.previewUrl) }, [src])
  useEffect(() => () => { if (out?.url) URL.revokeObjectURL(out.url) }, [out])

  async function pick(f: File | undefined) {
    setProblem(null)
    if (!f) return
    setWorking(true)
    try {
      const s = await loadSource(f)
      setSrc(s); setFrameAt(s.focal)
      setOut(await prepare(s, s.focal))
    } catch (e) {
      setSrc(null); setOut(null)
      setProblem(e instanceof Error ? e.message : 'That photo could not be prepared.')
    } finally { setWorking(false) }
  }

  async function reframe(at: number) {
    if (!src) return
    setWorking(true)
    try { setOut(await prepare(src, at)) } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That photo could not be prepared.')
    } finally { setWorking(false) }
  }

  // Dragging the 16:9 frame along the one direction it can move.
  function onDown(e: React.PointerEvent) {
    if (!src || src.axis === 'none') return
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    drag.current = { x: e.clientX, y: e.clientY, at: frameAt }
  }
  function onMove(e: React.PointerEvent) {
    if (!drag.current || !src || !box.current || src.travel <= 0) return
    const r = box.current.getBoundingClientRect()
    const d = src.axis === 'x'
      ? (e.clientX - drag.current.x) / r.width * src.w / src.travel
      : (e.clientY - drag.current.y) / r.height * src.h / src.travel
    setFrameAt(Math.min(1, Math.max(0, drag.current.at + d)))
  }
  function onUp() {
    if (!drag.current) return
    drag.current = null
    void reframe(frameAt)
  }

  const win = src && src.axis !== 'none' ? {
    left: src.axis === 'x' ? `${frameAt * src.travel / src.w * 100}%` : '0',
    top: src.axis === 'y' ? `${frameAt * src.travel / src.h * 100}%` : '0',
    width: `${src.cropW / src.w * 100}%`,
    height: `${src.cropH / src.h * 100}%`,
  } : null

  const replacing = siteId && hasPhoto.has(siteId)
  const siteName = active.find((s: any) => s.id === siteId)?.site_name

  async function save() {
    if (!out || !siteId) return
    if (!navigator.onLine) { toast.error('You are offline. Add the photo once you have a connection.'); return }
    setBusy(true)
    try {
      const d = new Date()
      const ext = out.mime === 'image/webp' ? 'webp' : 'jpg'
      const path = `${siteId}/${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}/${crypto.randomUUID()}.${ext}`
      const { error: ue } = await supabase.storage.from(BUCKET)
        .upload(path, out.blob, { contentType: out.mime, upsert: false, cacheControl: '31536000' })
      if (ue) throw new Error(ue.message)
      const { error: re } = await supabase.rpc('rpc_add_site_photo', {
        p_site_id: siteId, p_file_path: path, p_mime_type: out.mime,
        p_width: out.width, p_height: out.height, p_bytes: out.bytes,
        p_caption: caption.trim() || null,
      })
      if (re) throw new Error(re.message)
      toast.success(replacing ? `${siteName}: new photo on the dashboard; the old one is kept on record.`
                              : 'Added. Every head-office dashboard is showing it now.')
      await onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add the photo')
    } finally { setBusy(false) }
  }

  return (
    <Modal open wide onClose={onClose} title="Add a site photo"
           subtitle="One current photo per site - shown to head office on both dashboards"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={() => void save()} disabled={busy || working || !out || !siteId}>
               {busy ? 'Adding…' : replacing ? 'Replace the photo' : 'Add to the dashboard'}
             </button>
           </>}>
      <div className="grid gap-4 md:grid-cols-[1.5fr_1fr]">
        <div>
          {!src ? (
            <button type="button" onClick={() => input.current?.click()}
                    className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 hover:border-oval-400 hover:bg-oval-50">
              {working ? <Spinner className="h-6 w-6 text-oval-600" /> : <ImagePlus className="h-7 w-7 text-oval-600" />}
              <span className="text-sm font-semibold text-slate-700">{working ? 'Preparing…' : 'Choose a photo'}</span>
              <span className="text-xs">Any shape — it is framed to 16:9 around the subject for you</span>
            </button>
          ) : (
            <>
              <p className="label">Framing {src.axis !== 'none' && <span className="font-normal text-slate-500">— drag the frame if the subject is not centred in it</span>}</p>
              {/* The whole photo, dimmed, with the 16:9 frame that will be kept. */}
              <div ref={box} className="relative w-full select-none overflow-hidden rounded-xl border border-slate-200 bg-slate-900"
                   style={{ aspectRatio: `${src.w} / ${src.h}`, maxHeight: 360, touchAction: 'none' }}
                   onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
                <img src={src.previewUrl} alt="" draggable={false} className="h-full w-full object-contain opacity-90" />
                {win && (
                  <div className={clsx('absolute rounded-md ring-2 ring-white', src.axis === 'x' ? 'cursor-ew-resize' : 'cursor-ns-resize')}
                       style={{ ...win, boxShadow: '0 0 0 9999px rgba(14, 22, 20, 0.55)' }} />
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Wand2 className="h-3.5 w-3.5 text-oval-600" />
                  {src.axis === 'none' ? 'Already 16:9 — kept whole' : 'Framed around the subject automatically'}
                </span>
                <span className="flex gap-1">
                  {src.axis !== 'none' && Math.abs(frameAt - src.focal) > 0.001 && (
                    <button className="btn-ghost btn-sm" onClick={() => { setFrameAt(src.focal); void reframe(src.focal) }}>
                      <RotateCcw className="h-3.5 w-3.5" /> Automatic
                    </button>
                  )}
                  <button className="btn-ghost btn-sm" onClick={() => input.current?.click()}>Choose another</button>
                </span>
              </div>
            </>
          )}
          <input ref={input} type="file" accept="image/*" className="hidden"
                 onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = '' }} />
          {problem && <div className="mt-2"><Alert tone="error">{problem}</Alert></div>}
        </div>

        <div className="space-y-3">
          {out && (
            <div>
              <p className="label">As it will appear</p>
              <div className="relative overflow-hidden rounded-lg border border-slate-200">
                <img src={out.url} alt="How it will appear" className="aspect-video w-full object-cover" />
                {working && <div className="absolute inset-0 flex items-center justify-center bg-white/50"><Spinner /></div>}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                {out.mime === 'image/webp' ? 'WebP' : 'JPEG'} · {out.width}×{out.height} · <strong className="text-slate-700">{kb(out.bytes)}</strong>
                {src && <> (from {kb(src.originalBytes)})</>}
              </p>
            </div>
          )}
          <Field label="Which site?" required
                 hint={isPm ? 'Your own active sites. A colleague’s site is photographed by its Project Manager or head office.'
                            : 'Active sites only. Not listed? The administrator adds it under Master Data → Sites.'}>
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)} placeholder="Choose the site…"
                    options={active.map((s: any) => ({
                      value: s.id, label: hasPhoto.has(s.id) ? `${s.site_name} — has a photo` : s.site_name,
                    }))} />
          </Field>
          {replacing && (
            <Alert tone="info">This replaces the current photo of <strong>{siteName}</strong>. The old one is kept on record.</Alert>
          )}
          <Field label="Caption" hint="Optional, one line — what the photo shows">
            <Input value={caption} maxLength={120} onChange={(e) => setCaption(e.target.value)}
                   placeholder="e.g. Second-floor slab cast, 28-09-2026" />
          </Field>
        </div>
      </div>
    </Modal>
  )
}

function RemoveSitePhoto({ slide, onClose, onDone }: {
  slide: Slide; onClose: () => void; onDone: () => Promise<void>
}) {
  const toast = useToast()
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  return (
    <Modal open onClose={onClose} title="Take this photo off the dashboard?" subtitle={slide.site_name}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Keep it</button>
             <button className="btn-danger" disabled={busy} onClick={async () => {
               setBusy(true)
               const { error } = await supabase.rpc('rpc_remove_site_photo', { p_id: slide.id, p_reason: why.trim() || null })
               setBusy(false)
               if (error) { toast.error(error.message); return }
               await onDone()
             }}>{busy ? 'Removing…' : 'Take it down'}</button>
           </>}>
      <img src={slide.url} alt={slide.site_name} className="aspect-video w-full rounded-lg object-cover" />
      <div className="mt-3">
        <Field label="Why? (optional)" hint="Kept in the record. The photo is hidden, not deleted; the site shows no photo until a new one is added.">
          <Input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="e.g. Blurred" />
        </Field>
      </div>
    </Modal>
  )
}
